import { useState } from 'react';
import { Link } from 'react-router-dom';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { PLANS } from '@/shared/pricing';
import { GLOW, LABEL } from '@/components/home/styles';
import { cn } from '@/lib/utils';
import { useT } from '@/shared/i18n';
import { P } from '@/shared/locales/pages/keys';

/** Each "yes" counts toward one plan. `reason` is shown with the result. Both are translation keys. */
export const QUIZ = [
  { id: 'quantity', question: P.q1, plan: 'Wraith', reason: P.wMost },
  { id: 'sports-quality', question: P.q2, plan: 'Demon', reason: P.dSports },
  { id: 'multi', question: P.q3, plan: 'Demon', reason: P.rMulti },
  { id: 'europe', question: P.q4, plan: 'Demon', reason: P.rEU },
  { id: 'na-sports', question: P.q5, plan: 'Wraith', reason: P.rNA },
];

/** The plan the answers point to, or null on a tie. Wraith is one screen, so needing more decides it. */
export function quizResult(answers) {
  const yes = QUIZ.filter((q) => answers[q.id] === true);
  const score = { Wraith: 0, Demon: 0 };
  yes.forEach((q) => {
    score[q.plan] += 1;
  });
  const forced = answers.multi === true;
  let plan = null;
  if (forced || score.Demon > score.Wraith) plan = 'Demon';
  else if (score.Wraith > score.Demon) plan = 'Wraith';
  return { plan, forced, score, reasons: yes };
}

function Result({ answers, onRestart }) {
  const { t } = useT();
  const { plan, forced, reasons } = quizResult(answers);
  const other = plan === 'Demon' ? 'Wraith' : 'Demon';
  const backing = plan ? reasons.filter((q) => q.plan === plan) : [];
  const against = plan ? reasons.filter((q) => q.plan === other) : [];

  return (
    <div aria-live="polite">
      <p className={LABEL}>{t(P.yourMatch)}</p>
      {plan ? (
        <>
          <h3 className={cn('mt-3 font-heading text-6xl leading-none md:text-7xl', GLOW)}>{PLANS[plan].name}</h3>
          <p className="mt-3 text-lg text-foreground/85">{t(PLANS[plan].tagline)}</p>
          {backing.length > 0 && (
            <ul className="mt-6 space-y-2.5 text-[15px] text-foreground/85">
              {backing.map((q) => (
                <li key={q.id} className="flex items-start gap-3">
                  <ApperIcon name="Check" size={16} className="mt-1 shrink-0 text-primary" />
                  {t(q.reason)}
                </li>
              ))}
            </ul>
          )}
          {forced && against.length > 0 && (
            <p className="mt-5 max-w-xl text-sm text-muted-foreground">
              {t(P.lean, { other: PLANS[other].name, plan: PLANS[plan].name })}
            </p>
          )}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button size="lg" asChild>
              <Link
                to={
                  plan === 'Demon'
                    ? `/checkout?plan=Demon&term=1y&connections=${answers.multi ? 2 : 1}`
                    : '/checkout?plan=Wraith&term=12m&connections=1'
                }
              >
                {t(`Summon the ${PLANS[plan].name}`)}
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <a href="#compare-prices">{t(P.seePrices, { name: PLANS[plan].name })}</a>
            </Button>
          </div>
        </>
      ) : (
        <>
          <h3 className="mt-3 text-[clamp(32px,5vw,48px)] font-light leading-tight">{t(P.bothFit)}</h3>
          <p className="mt-3 max-w-xl text-[15px] leading-7 text-muted-foreground">
            {t(P.split, { a: PLANS.Wraith.name, b: PLANS.Demon.name })}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button size="lg" asChild>
              <a href="#compare-prices">{t(P.comparePrices)}</a>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to={{ pathname: '/', hash: '#contact' }}>{t(P.msgUs)}</Link>
            </Button>
          </div>
        </>
      )}
      <button
        type="button"
        onClick={onRestart}
        className="mt-8 inline-flex items-center gap-2 text-sm text-muted-foreground underline-offset-4 hover:text-primary hover:underline"
      >
        <ApperIcon name="RotateCcw" size={14} />
        {t(P.startOver)}
      </button>
    </div>
  );
}

/** Five yes/no questions that point to Wraith or Demon. */
export function PlanQuiz() {
  const { t } = useT();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const done = step >= QUIZ.length;
  const q = QUIZ[step];

  function answer(value) {
    setAnswers((a) => ({ ...a, [q.id]: value }));
    setStep((s) => s + 1);
  }

  function restart() {
    setAnswers({});
    setStep(0);
  }

  return (
    <section id="quiz" className="scroll-mt-24 rounded-lg border border-border bg-card p-6 md:p-10" aria-labelledby="quiz-title">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 id="quiz-title" className="text-[clamp(26px,4vw,36px)] font-light leading-tight">
          {t(P.quizTitle)}
        </h2>
        {!done && (
          <p className="text-sm tabular-nums text-muted-foreground">
            {t(P.qOf, { n: step + 1, total: QUIZ.length })}
          </p>
        )}
      </div>

      <div className="mt-4 flex gap-1.5" aria-hidden="true">
        {QUIZ.map((x, i) => (
          <span key={x.id} className={cn('h-1 flex-1 rounded-full', i < step ? 'bg-primary' : 'bg-muted')} />
        ))}
      </div>

      <div className="mt-8">
        {done ? (
          <Result answers={answers} onRestart={restart} />
        ) : (
          <div key={q.id}>
            <p className="max-w-2xl text-xl leading-snug text-foreground md:text-2xl">{t(q.question)}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button size="lg" className="min-w-28" onClick={() => answer(true)}>
                {t(P.yes)}
              </Button>
              <Button size="lg" variant="outline" className="min-w-28" onClick={() => answer(false)}>
                {t(P.no)}
              </Button>
              {step > 0 && (
                <Button size="lg" variant="ghost" onClick={() => setStep((s) => s - 1)}>
                  <ApperIcon name="ArrowLeft" size={16} className="rtl:rotate-180" />
                  {t(P.backBtn)}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
